import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Avatar,
  Chip,
  Tab,
  Tabs
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  LocationOn,
  BusinessCenter,
  Call,
  MoreVert,
  NorthEast,
  InfoOutlined,
  SouthWest,
  QrCode2,
  CloudUpload,
  Delete,
  ZoomIn,
  ZoomOut,
  RestartAlt,
  FileDownload
} from "@mui/icons-material";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import Page from "src/components/Page";
import Label from "src/components/Label";
import CustomLoader from "src/components/CustomLoader";
import {
  getReferralMemberDetail,
  getReferralMemberWalletSetting,
  updateReferralWithdrawalRequestStatus
} from "src/api/commonapi";
import { notification } from "src/utils/messages";
import { Routes } from "src/constant/api_url";

const formatCurrency = (amount) => {
  const num = parseFloat(amount);
  if (isNaN(num)) return "₹0";
  return `₹${num.toLocaleString("en-IN")}`;
};

const getStatusBadge = (status) => {
  switch (status) {
    case 0:
      return { label: "Pending", color: "warning" };
    case 1:
      return { label: "Successful", color: "success" };
    case 2:
      return { label: "Rejected", color: "error" };
    default:
      return { label: "Unknown", color: "default" };
  }
};

const getReferralTypeBadge = (type) => {
  switch (type) {
    case 0:
      return { label: "LVB Lead", color: "warning" };
    case 1:
      return { label: "Prime Lead", color: "info" };
    case 2:
      return { label: "Full Member", color: "success" };
    default:
      return { label: "Unknown", color: "default" };
  };
};

const getPayoutTypeLabel = (payoutType) => {
  switch (payoutType) {
    case 0:
      return "UPI";
    case 1:
      return "Bank";
    default:
      return "-";
  }
};

const maskAccountNumber = (acNo) => {
  if (!acNo || acNo.length <= 4) return acNo;
  return "*".repeat(acNo.length - 4) + acNo.slice(-4);
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export default function ReferralReportDetail() {
  const { m_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [memberInfo, setMemberInfo] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "wallet") return 1;
    if (tabParam === "settings") return 2;
    return 0;
  });

  const [referrals, setReferrals] = useState({
    data: [],
    total: 0,
    page: 1,
    loading: false,
    r_status: -1
  });

  const [walletHistory, setWalletHistory] = useState({
    data: [],
    total: 0,
    page: 1,
    loading: false,
    w_status: -1
  });

  const [withdrawals, setWithdrawals] = useState({
    data: [],
    total: 0,
    page: 1,
    loading: false
  });

  const [walletSettings, setWalletSettings] = useState({
    loading: false,
    data: {},
    error: null
  });

  const [qrModal, setQrModal] = useState(false);
  const [qrRow, setQrRow] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState("");
  const screenshotInputRef = useRef(null);
  const [screenshotModal, setScreenshotModal] = useState(false);
  const [screenshotViewUrl, setScreenshotViewUrl] = useState("");
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, panX: 0, panY: 0 });

  const [modalState, setModalState] = useState({
    showApprove: false,
    showReject: false,
    selectedRequest: null,
    rejectReason: "",
    processing: false,
    error: null
  });

  const perPage = 20;

  const fetchMemberInfo = useCallback(async () => {
    try {
      const response = await getReferralMemberDetail({
        m_id: parseInt(m_id),
        page: 1,
        per_page: 1,
        tab: "referrals"
      });
      if (response.status == 1) {
        setMemberInfo(response.data.member_info);
      }
    } catch {
      // handled below
    }
    setLoading(false);
  }, [m_id]);

  const fetchTabData = useCallback(
    async (tab, pageNum = 1) => {
      try {
        const params = {
          m_id: parseInt(m_id),
          page: pageNum,
          per_page: perPage,
          tab
        };
        if (tab === "referrals") {
          params.r_status = referrals.r_status;
        }

        const response = await getReferralMemberDetail(params);
        if (response.status == 1) {
          const tabData = response.data;
          if (tabData.member_info) setMemberInfo(tabData.member_info);

          if (tab === "referrals") {
            setReferrals((prev) => ({
              ...prev,
              data: tabData.referrals || [],
              total: tabData.referrals_total || 0,
              page: pageNum,
              loading: false
            }));
          } else if (tab === "wallet") {
            setWalletHistory((prev) => ({
              ...prev,
              data: tabData.wallet_history || [],
              total: tabData.wallet_history_total || 0,
              page: pageNum,
              loading: false
            }));
          } else if (tab === "withdrawals") {
            setWithdrawals((prev) => ({
              ...prev,
              data: tabData.withdrawal_requests || [],
              total: tabData.withdrawals_total || 0,
              page: pageNum,
              loading: false
            }));
          }
        }
      } catch {
        if (tab === "referrals") {
          setReferrals((prev) => ({ ...prev, loading: false }));
        } else if (tab === "wallet") {
          setWalletHistory((prev) => ({ ...prev, loading: false }));
        } else if (tab === "withdrawals") {
          setWithdrawals((prev) => ({ ...prev, loading: false }));
        }
      }
    },
    [m_id, referrals.r_status]
  );

  const fetchWalletSettings = useCallback(async () => {
    setWalletSettings((prev) => ({ ...prev, loading: true }));
    try {
      const response = await getReferralMemberWalletSetting({
        m_id: parseInt(m_id)
      });
      if (response.status == 1) {
        setWalletSettings({ loading: false, data: response.data || {}, error: null });
      } else {
        setWalletSettings((prev) => ({ ...prev, loading: false, error: response.message }));
      }
    } catch (error) {
      setWalletSettings((prev) => ({ ...prev, loading: false, error: error.message }));
    }
  }, [m_id]);

  useEffect(() => {
    setLoading(true);
    fetchMemberInfo();
    fetchTabData("referrals");
    if (activeTab === 1) {
      fetchTabData("wallet");
      fetchTabData("withdrawals");
    } else if (activeTab === 2) {
      fetchWalletSettings();
    }
  }, [fetchMemberInfo, fetchTabData, fetchWalletSettings]);

  const handleTabChange = (_, newValue) => {
    const tabs = ["referrals", "wallet", "settings"];
    const tab = tabs[newValue];
    setActiveTab(newValue);

    if (tab === "settings") {
      fetchWalletSettings();
    } else if (tab === "wallet") {
      if (walletHistory.data.length === 0 || withdrawals.data.length === 0) {
        fetchTabData("wallet");
        fetchTabData("withdrawals");
      }
    } else if (tab === "referrals" && referrals.data.length === 0) {
      fetchTabData("referrals");
    }
  };

  const handleReferralStatusFilter = (rStatus) => {
    setReferrals((prev) => ({ ...prev, loading: true, r_status: rStatus, page: 1 }));
    getReferralMemberDetail({
      m_id: parseInt(m_id),
      page: 1,
      per_page: perPage,
      r_status: rStatus,
      tab: "referrals"
    })
      .then((response) => {
        if (response.status == 1) {
          setReferrals({
            data: response.data.referrals || [],
            total: response.data.referrals_total || 0,
            page: 1,
            loading: false,
            r_status: rStatus
          });
        }
      })
      .catch(() => {
        setReferrals((prev) => ({ ...prev, loading: false }));
      });
  };

  const handleReferralsPageChange = (_, newPage) => {
    const pageNum = newPage + 1;
    setReferrals((prev) => ({ ...prev, loading: true }));
    fetchTabData("referrals", pageNum);
  };

  const handleWalletPageChange = (_, newPage) => {
    const pageNum = newPage + 1;
    setWalletHistory((prev) => ({ ...prev, loading: true }));
    fetchTabData("wallet", pageNum);
  };

  const handleWalletStatusFilter = (wStatus) => {
    setWalletHistory((prev) => ({ ...prev, w_status: wStatus }));
  };

  const filteredWalletData = walletHistory.data.filter((row) => {
    if (walletHistory.w_status === -1) return true;
    const statusMap = { approved: 1, rejected: 2 };
    const rowStatus = (row.status || "").toLowerCase();
    return statusMap[rowStatus] === walletHistory.w_status;
  });

  const handleApproveClick = (request) => {
    setPaymentScreenshot(null);
    setScreenshotPreview("");
    setModalState({
      showApprove: true,
      showReject: false,
      selectedRequest: request,
      rejectReason: "",
      processing: false,
      error: null
    });
  };

  const handleApproveConfirm = async () => {
    if (!paymentScreenshot) {
      setModalState((prev) => ({ ...prev, error: "Please upload payment screenshot" }));
      return;
    }
    setModalState((prev) => ({ ...prev, processing: true, error: null }));
    try {
      const formData = new FormData();
      formData.append("wr_amt_r_id", modalState.selectedRequest.wr_amt_r_id);
      formData.append("m_id", parseInt(m_id));
      formData.append("status", 1);
      formData.append("payment_screenshot", paymentScreenshot);
      const response = await updateReferralWithdrawalRequestStatus(formData);
      const notify = notification({
        type: response.status == 0 ? "error" : "success",
        message: response.message
      });
      notify();
      if (response.status == 1) {
        setModalState((prev) => ({ ...prev, showApprove: false, processing: false }));
        setPaymentScreenshot(null);
        setScreenshotPreview("");
        fetchTabData("withdrawals", withdrawals.page);
        fetchTabData("wallet", walletHistory.page);
      } else {
        setModalState((prev) => ({ ...prev, processing: false }));
      }
    } catch (error) {
      const notify = notification({ type: "error", message: error.message || "Something went wrong" });
      notify();
      setModalState((prev) => ({ ...prev, processing: false }));
    }
  };

  const handleRejectClick = (request) => {
    setModalState({
      showApprove: false,
      showReject: true,
      selectedRequest: request,
      rejectReason: "",
      processing: false,
      error: null
    });
  };

  const handleRejectConfirm = async () => {
    if (!modalState.rejectReason.trim()) {
      setModalState((prev) => ({ ...prev, error: "Reason is required" }));
      return;
    }
    setModalState((prev) => ({ ...prev, processing: true, error: null }));
    try {
      const response = await updateReferralWithdrawalRequestStatus({
        wr_amt_r_id: modalState.selectedRequest.wr_amt_r_id,
        m_id: parseInt(m_id),
        status: 2,
        rejected_reason: modalState.rejectReason.trim()
      });
      const notify = notification({
        type: response.status == 0 ? "error" : "success",
        message: response.message
      });
      notify();
      if (response.status == 1) {
        setModalState((prev) => ({ ...prev, showReject: false, processing: false }));
        fetchTabData("withdrawals", withdrawals.page);
        fetchTabData("wallet", walletHistory.page);
      } else {
        setModalState((prev) => ({ ...prev, processing: false }));
      }
    } catch (error) {
      const notify = notification({ type: "error", message: error.message || "Something went wrong" });
      notify();
      setModalState((prev) => ({ ...prev, processing: false }));
    }
  };

  const handleModalClose = () => {
    if (modalState.processing) return;
    setModalState({
      showApprove: false,
      showReject: false,
      selectedRequest: null,
      rejectReason: "",
      processing: false,
      error: null
    });
    setPaymentScreenshot(null);
    setScreenshotPreview("");
  };

  const openQrModal = async (row) => {
    setQrRow(row);
    setQrModal(true);
    try {
      const QRCode = await import("qrcode");
      const upiUrl = `upi://pay?pa=${row.upi_id}&cu=INR&am=${row.amount}`;
      const dataUrl = await QRCode.toDataURL(upiUrl, { width: 256, margin: 2 });
      setQrDataUrl(dataUrl);
    } catch {
      setQrDataUrl("");
    }
  };

  // --- Referral columns ---
  const referralColumns = [
    {
      name: "#",
      selector: (row, index) => (referrals.page - 1) * perPage + index + 1,
      width: "60px"
    },
    {
      name: "REFERRED MEMBER",
      cell: (row) => (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <Avatar
              src={row?.photo ? `${Routes.API_HOSTNAME}images/member/${row.photo}` : undefined}
              sx={{
                width: 32,
                height: 32,
                bgcolor: "grey.300",
                fontSize: 12,
                fontWeight: 600
              }}
            >
              {getInitials(row.referred_member_name)}
            </Avatar>
            {(row?.m_verify_status === 1 || row?.m_verify_status === 2 || row?.m_verify_status === 3) && (
              <Box
                component="img"
                src={row?.m_verify_status === 1 ? "/static/icons/verified.png" : row?.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified_orange.png"}
                alt="Verified"
                sx={{ width: 14, height: 14, position: "absolute", top: -1, right: -1 }}
              />
            )}
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={600}>{row.referred_member_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.mobile_no}
            </Typography>
          </Box>
        </Stack>
      ),
      maxWidth: "300px"
    },
    {
      name: "AMOUNT (₹)",
      selector: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {formatCurrency(row.amount)}
        </Typography>
      ),
      center: true
    },
    {
      name: "Registered Date",
      selector: (row) => row.registered_date,
      center: true
    },
    {
      name: "Membership Date",
      selector: (row) => row.membership_date,
      center: true
    },
    {
      name: "STATUS",
      cell: (row) => {
        const badge = getStatusBadge(row.r_status);
        return (
          <Label variant="ghost" color={badge.color}>
            {badge.label}
          </Label>
        );
      },
      center: true
    }
  ];

  const getWalletTransactionStyle = (row) => {
    const type = (row.type || "").toLowerCase();
    const status = (row.status || "").toLowerCase();
    if (type === "withdrawal") {
      if (status === "rejected") {
        return { IconComp: NorthEast, bgColor: "rgba(220, 53, 69, 0.08)", iconColor: "#dc3545", amountColor: "#dc3545", statusColor: "#dc3545" };
      }
      if (status === "pending") {
        return { IconComp: NorthEast, bgColor: "rgba(255, 193, 7, 0.08)", iconColor: "#f59e0b", amountColor: "#f59e0b", statusColor: "#f59e0b" };
      }
      return { IconComp: NorthEast, bgColor: "rgba(40, 167, 69, 0.08)", iconColor: "#28a745", amountColor: "#28a745", statusColor: "#28a745" };
    }
    return { IconComp: SouthWest, bgColor: "rgba(40, 167, 69, 0.08)", iconColor: "#28a745", amountColor: "#28a745", statusColor: "#28a745" };
  };

  // --- Withdrawal columns ---
  const withdrawalColumns = [
    {
      name: "#",
      selector: (row, index) => (withdrawals.page - 1) * perPage + index + 1,
      width: "60px"
    },
    {
      name: "AMOUNT",
      selector: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {formatCurrency(row.amount)}
        </Typography>
      ),
      width: "100px",
      center: true
    },
    {
      name: "TYPE",
      cell: (row) => (
        <Label variant="ghost" color="default">
          {getPayoutTypeLabel(row.payout_type)}
        </Label>
      ),
      width: "90px",
      center: true
    },
    {
      name: "REQUESTED DATE",
      selector: (row) => row.requested_date || "-",
      center: true
    },
    {
      name: "APPROVED DATE",
      selector: (row) => (row.status == 1 ? row.approved_date || "-" : "-"),
      center: true
    },
    {
      name: "REJECTED DATE",
      selector: (row) => (row.status == 2 ? row.rejected_date || "-" : "-"),
      center: true
    },
    {
      name: "PAYMENT SCREENSHOT",
      cell: (row) =>
        row.payment_screenshot ? (
          <Box
            component="img"
            src={`${Routes.API_HOSTNAME}referral-p-screenshot/${row.payment_screenshot}`}
            alt="Screenshot"
            onClick={() => {
              setScreenshotViewUrl(`${Routes.API_HOSTNAME}referral-p-screenshot/${row.payment_screenshot}`);
              setScreenshotModal(true);
            }}
            sx={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "divider",
              cursor: "pointer",
              "&:hover": { opacity: 0.8 }
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
      center: true
    },
    {
      name: "REJECTED REASON",
      selector: (row) => (row.status == 2 ? row.rejected_reason || "-" : "-"),
      minWidth: "150px"
    },
    {
      name: "STATUS",
      cell: (row) => {
        const badge = getStatusBadge(row.status);
        return (
          <Label variant="ghost" color={badge.color}>
            {badge.label}
          </Label>
        );
      },
      width: "110px",
      center: true
    },
    {
      name: "Action",
      wrap: true,
      cell: (row) =>
      (
        <Stack direction="row" spacing={0}>
          {row.payout_type == 0 && row.upi_id && (
            <Tooltip title="View QR Code">
              <IconButton size="small" onClick={() => openQrModal(row)}>
                <QrCode2 color="primary" fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Approve">
            <IconButton size="small" onClick={() => handleApproveClick(row)}>
              <CheckCircle color="success" fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject">
            <IconButton size="small" onClick={() => handleRejectClick(row)}>
              <Cancel color="error" fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
      width: "160px",
      center: true
    }
  ];

  // --- Referral Export ---
  const referralExportHeaders = [
    { label: "Referred Member", key: "referred_member_name" },
    { label: "Mobile No", key: "mobile_no" },
    { label: "Amount", key: "amount" },
    { label: "Status", key: "status_label" },
    { label: "Registered Date", key: "registered_date" },
    { label: "Membership Date", key: "membership_date" }
  ];

  const referralExportData = referrals.data.map((row) => ({
    referred_member_name: row.referred_member_name || "",
    mobile_no: row.mobile_no || "",
    amount: row.amount || 0,
    status_label: getStatusBadge(row.r_status).label,
    registered_date: row.registered_date || "",
    membership_date: row.membership_date || ""
  }));

  // --- Withdrawals Export ---
  const withdrawalExportHeaders = [
    { label: "Amount", key: "amount" },
    { label: "Type", key: "payout_type_label" },
    { label: "Requested Date", key: "requested_date" },
    { label: "Approved Date", key: "approved_date" },
    { label: "Rejected Date", key: "rejected_date" },
    { label: "Status", key: "status_label" },
    { label: "UPI ID", key: "upi_id" },
    { label: "Payment Screenshot", key: "payment_screenshot" },
    { label: "Rejected Reason", key: "rejected_reason" }
  ];

  const withdrawalExportData = withdrawals.data.filter((w) => w.status === 0).map((row) => ({
    amount: row.amount || 0,
    payout_type_label: getPayoutTypeLabel(row.payout_type),
    requested_date: row.requested_date || "",
    approved_date: row.status == 1 ? row.approved_date || "" : "",
    rejected_date: row.status == 2 ? row.rejected_date || "" : "",
    status_label: getStatusBadge(row.status).label,
    upi_id: row.upi_id || "",
    payment_screenshot: row.payment_screenshot ? `${Routes.API_HOSTNAME}referral-p-screenshot/${row.payment_screenshot}` : "",
    rejected_reason: row.status == 2 ? row.rejected_reason || "" : ""
  }));

  // --- Wallet History Export ---
  const walletExportHeaders = [
    { label: "Description", key: "description" },
    { label: "Amount", key: "amount" },
    { label: "Type", key: "type" },
    { label: "Status", key: "status" },
    { label: "Date", key: "transaction_date" }
  ];

  const walletExportData = filteredWalletData.map((row) => ({
    description: row.description || "",
    amount: row.amount || 0,
    type: row.type || "",
    status: row.status || "",
    transaction_date: row.transaction_date || ""
  }));

  const tabLabels = ["Referral History", "Wallet & Withdrawals", "Member Bank Details"];

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <Card sx={{ overflow: "hidden" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, pt: 2 }}>
              <Stack direction="row" spacing={1}>
                {[
                  { label: "All", value: -1 },
                  { label: "Pending", value: 0 },
                  { label: "Successful", value: 1 }
                ].map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    onClick={() => handleReferralStatusFilter(opt.value)}
                    variant={referrals.r_status === opt.value ? "filled" : "outlined"}
                    color={referrals.r_status === opt.value ? "primary" : "default"}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Stack>
              {referralExportData.length > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FileDownload fontSize="small" />}
                  sx={{ textTransform: "none" }}
                >
                  <CSVLink
                    data={referralExportData}
                    headers={referralExportHeaders}
                    filename={`Referral History - ${memberInfo?.m_name || "Member"}.csv`}
                    style={{ textDecoration: "none", color: "inherit" }}
                    target="_blank"
                  >
                    Export CSV
                  </CSVLink>
                </Button>
              )}
            </Stack>
            <DataTable
              pagination
              paginationServer
              paginationTotalRows={referrals.total}
              onChangePage={handleReferralsPageChange}
              paginationPerPage={perPage}
              highlightOnHover
              columns={referralColumns}
              data={referrals.data}
              progressPending={referrals.loading}
              progressComponent={<CustomLoader />}
              noDataComponent={
                <Box py={5}>
                  <Typography variant="body1" color="text.secondary">
                    No referral history found
                  </Typography>
                </Box>
              }
            />
          </Card>
        );

      case 1: {
        const pendingWithdrawals = withdrawals.data.filter((w) => w.status === 0);
        const totalPending = pendingWithdrawals.length;
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Pending Withdrawal Requests Section */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="h6" fontWeight={600}>
                    Pending Withdrawal Requests
                  </Typography>
                  {totalPending > 0 && (
                    <Chip
                      label={`${totalPending} Pending`}
                      size="small"
                      sx={{
                        bgcolor: "rgba(0, 111, 234, 0.1)",
                        color: "primary.main",
                        fontWeight: 500,
                        fontSize: 12
                      }}
                    />
                  )}
                </Stack>
                {withdrawalExportData.length > 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<FileDownload fontSize="small" />}
                    sx={{ textTransform: "none" }}
                  >
                    <CSVLink
                      data={withdrawalExportData}
                      headers={withdrawalExportHeaders}
                      filename={`Withdrawals - ${memberInfo?.m_name || "Member"}.csv`}
                      style={{ textDecoration: "none", color: "inherit" }}
                      target="_blank"
                    >
                      Export CSV
                    </CSVLink>
                  </Button>
                )}
              </Stack>
              {withdrawals.loading ? (
                <Card sx={{ p: 4, textAlign: "center" }}>
                  <CircularProgress />
                </Card>
              ) : totalPending === 0 ? (
                <Card sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No pending withdrawal requests
                  </Typography>
                </Card>
              ) : (
                <Card sx={{ overflow: "hidden" }}>
                  <DataTable
                    highlightOnHover
                    columns={withdrawalColumns}
                    data={pendingWithdrawals}
                    progressPending={withdrawals.loading}
                    progressComponent={<CustomLoader />}
                    noDataComponent={
                      <Box py={5}>
                        <Typography variant="body1" color="text.secondary">
                          No pending requests
                        </Typography>
                      </Box>
                    }
                  />
                </Card>
              )}
            </Box>

            {/* Transaction History Section */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6" fontWeight={600}>
                    Transaction History
                  </Typography>
                  {[
                    { label: "All", value: -1 },
                    { label: "Approved", value: 1 },
                    { label: "Rejected", value: 2 }
                  ].map((opt) => (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      onClick={() => handleWalletStatusFilter(opt.value)}
                      variant={walletHistory.w_status === opt.value ? "filled" : "outlined"}
                      color={walletHistory.w_status === opt.value ? "primary" : "default"}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  ))}
                </Stack>
                {walletExportData.length > 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<FileDownload fontSize="small" />}
                    sx={{ textTransform: "none" }}
                  >
                    <CSVLink
                      data={walletExportData}
                      headers={walletExportHeaders}
                      filename={`Wallet History - ${memberInfo?.m_name || "Member"}.csv`}
                      style={{ textDecoration: "none", color: "inherit" }}
                      target="_blank"
                    >
                      Export CSV
                    </CSVLink>
                  </Button>
                )}
              </Stack>
              {walletHistory.loading ? (
                <Card sx={{ p: 4, textAlign: "center" }}>
                  <CircularProgress />
                </Card>
              ) : filteredWalletData.length === 0 ? (
                <Card sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    {walletHistory.w_status === -1 ? "No transaction history found" : "No transactions found for selected filter"}
                  </Typography>
                </Card>
              ) : (
                <>
                  <Stack spacing={2}>
                    {filteredWalletData.map((row, idx) => {
                      const style = getWalletTransactionStyle(row);
                      const isCredit = (row.type || "").toLowerCase() !== "withdrawal";
                      const isRejected =
                        (row.type || "").toLowerCase() === "withdrawal" &&
                        (row.status || "").toLowerCase() === "rejected";
                      return (
                        <Box key={idx}>
                          <Card
                            variant="outlined"
                            sx={{
                              p: 2.5,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              border: "1px solid",
                              borderColor: "divider"
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={2}
                              sx={{ flex: 1, minWidth: 0 }}
                            >
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: "50%",
                                  bgcolor: style.bgColor,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0
                                }}
                              >
                                <style.IconComp sx={{ color: style.iconColor, fontSize: 22 }} />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body1" fontWeight={600} noWrap>
                                  {row.description || "-"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {row.transaction_date || "-"}
                                </Typography>
                              </Box>
                            </Stack>
                            <Stack alignItems="flex-end" sx={{ flexShrink: 0, ml: 2 }}>
                              <Typography
                                variant="body1"
                                fontWeight={700}
                                color={style.amountColor}
                              >
                                {isCredit ? "+" : "-"} {formatCurrency(row.amount)}
                              </Typography>
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                color={style.statusColor}
                              >
                                {(row.status || "").charAt(0).toUpperCase() +
                                  (row.status || "").slice(1)}
                              </Typography>
                            </Stack>
                          </Card>
                          {isRejected && row.rejection_reason && (
                            <Box
                              sx={{
                                mt: -0.25,
                                mx: 1,
                                px: 1.5,
                                py: 0.75,
                                bgcolor: "rgba(220, 53, 69, 0.06)",
                                border: "1px solid rgba(220, 53, 69, 0.2)",
                                borderRadius: "0 0 8px 8px",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75
                              }}
                            >
                              <InfoOutlined sx={{ fontSize: 16, color: "#dc3545" }} />
                              <Typography variant="caption" color="#dc3545">
                                {row.rejection_reason}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                  {walletHistory.total > perPage && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        borderTop: "1px solid",
                        borderColor: "divider",
                        px: 2
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          py: 1.5,
                          color: "text.secondary",
                          fontSize: 13
                        }}
                      >
                        <span>
                          Showing {walletHistory.page * perPage - (perPage - 1)}–
                          {Math.min(walletHistory.page * perPage, walletHistory.total)} of{" "}
                          {walletHistory.total}
                        </span>
                        <Button
                          size="small"
                          disabled={walletHistory.page <= 1}
                          onClick={() =>
                            handleWalletPageChange(null, walletHistory.page - 2)
                          }
                          sx={{ textTransform: "none", minWidth: "auto", py: 0 }}
                        >
                          Prev
                        </Button>
                        <Button
                          size="small"
                          disabled={walletHistory.page * perPage >= walletHistory.total}
                          onClick={() =>
                            handleWalletPageChange(null, walletHistory.page)
                          }
                          sx={{ textTransform: "none", minWidth: "auto", py: 0 }}
                        >
                          Next
                        </Button>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        );
      }

      case 2:
        return (
          <Card sx={{ p: 3 }}>
            {walletSettings.loading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress />
              </Box>
            ) : walletSettings.data && Object.keys(walletSettings.data).length > 0 ? (
              <Grid container spacing={3}>
                {walletSettings.data.payee_name && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                      Payee Name
                    </Typography>
                    <Typography variant="body1" fontWeight={600} mt={0.5}>
                      {walletSettings.data.payee_name}
                    </Typography>
                  </Grid>
                )}
                {walletSettings.data.upi_id && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                      UPI ID
                    </Typography>
                    <Typography variant="body1" fontWeight={600} mt={0.5}>
                      {walletSettings.data.upi_id}
                    </Typography>
                  </Grid>
                )}
                {walletSettings.data.bank_name && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                      Bank Name
                    </Typography>
                    <Typography variant="body1" fontWeight={600} mt={0.5}>
                      {walletSettings.data.bank_name}
                    </Typography>
                  </Grid>
                )}
                {walletSettings.data.ac_no && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                      Account Number
                    </Typography>
                    <Typography variant="body1" fontWeight={600} mt={0.5}>
                      {maskAccountNumber(walletSettings.data.ac_no)}
                    </Typography>
                  </Grid>
                )}
                {walletSettings.data.ifsc_code && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                      IFSC Code
                    </Typography>
                    <Typography variant="body1" fontWeight={600} mt={0.5}>
                      {walletSettings.data.ifsc_code}
                    </Typography>
                  </Grid>
                )}
                {walletSettings.data.branch_name && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                      Branch
                    </Typography>
                    <Typography variant="body1" fontWeight={600} mt={0.5}>
                      {walletSettings.data.branch_name}
                    </Typography>
                  </Grid>
                )}
                {walletSettings.data.account_holder_name && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                      Account Holder
                    </Typography>
                    <Typography variant="body1" fontWeight={600} mt={0.5}>
                      {walletSettings.data.account_holder_name}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Box py={5} textAlign="center">
                <Typography variant="body1" color="text.secondary">
                  No wallet settings configured
                </Typography>
              </Box>
            )}
          </Card>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Page title="Referrer Detail | Local Vocal Business Group">
        <Container>
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress />
          </Box>
        </Container>
      </Page>
    );
  }

  if (!memberInfo) {
    return (
      <Page title="Referrer Detail | Local Vocal Business Group">
        <Container>
          <Box py={5} textAlign="center">
            <Typography variant="h5" color="text.secondary" mb={2}>
              Member not found
            </Typography>
            <Button variant="contained" onClick={() => navigate("/referral-report")} sx={{ textTransform: "none" }}>
              Back to Report
            </Button>
          </Box>
        </Container>
      </Page>
    );
  }

  return (
    <Page title={`${memberInfo.m_name || "Referrer"} Detail | Local Vocal Business Group`}>
      <Container>
        {/* Back Navigation */}
        <Box mb={3}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/referral-report")}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Back to Report
          </Button>
        </Box>

        {/* Profile Header */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={3}>
            <Stack direction="row" alignItems="center" spacing={2.5}>
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <Avatar
                  src={memberInfo?.photo ? `${Routes.API_HOSTNAME}images/member/${memberInfo.photo}` : undefined}
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: "primary.main",
                    fontSize: 22,
                    fontWeight: 700,
                    border: "2px solid",
                    borderColor: "primary.light"
                  }}
                >
                  {getInitials(memberInfo.m_name)}
                </Avatar>
                {(memberInfo?.m_verify_status === 1 || memberInfo?.m_verify_status === 2 || memberInfo?.m_verify_status === 3) && (
                  <Box
                    component="img"
                    src={memberInfo?.m_verify_status === 1 ? "/static/icons/verified.png" : memberInfo?.m_verify_status === 2 ? "/static/icons/verified_blue.png" : "/static/icons/verified_orange.png"}
                    alt="Verified"
                    sx={{ width: 18, height: 18, position: "absolute", top: -1, right: -1 }}
                  />
                )}
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {memberInfo.m_name}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2} mt={0.5} flexWrap="wrap">
                  {memberInfo.city_name && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <LocationOn sx={{ fontSize: 16, color: "text.secondary" }} />
                      <Typography variant="body2" color="text.secondary">
                        {memberInfo.city_name}
                      </Typography>
                    </Stack>
                  )}
                  {memberInfo.company_name && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <BusinessCenter sx={{ fontSize: 16, color: "text.secondary" }} />
                      <Typography variant="body2" color="text.secondary">
                        {memberInfo.company_name}
                      </Typography>
                    </Stack>
                  )}
                  {memberInfo.mobile_no && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Call sx={{ fontSize: 16, color: "text.secondary" }} />
                      <Typography variant="body2" color="text.secondary">
                        {memberInfo.mobile_no}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Stack>
            {memberInfo.referral_code && (
              <Card
                variant="outlined"
                sx={{ px: 3, py: 2, minWidth: 180, textAlign: "center" }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                  Referral Code
                </Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontFamily: "monospace", letterSpacing: 2 }}>
                  {memberInfo.referral_code}
                </Typography>
              </Card>
            )}
          </Stack>
        </Card>

        {/* Summary Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: "Total Refs", value: memberInfo.total_referral_count || 0, color: "primary.main" },
            { label: "Successful", value: memberInfo.successful_referral_count || 0, color: "success.main" },
            { label: "Earned", value: formatCurrency(memberInfo.earned_amount), color: "warning.main" },
            { label: "Withdrawn", value: formatCurrency(memberInfo.total_withdrawn), color: "grey.600" },
            { label: "Withdrawable", value: formatCurrency(memberInfo.withdrawable_amount), color: "primary.dark" }
          ].map((stat, idx) => (
            <Grid item xs={6} sm={4} md key={idx}>
              <Card
                sx={{
                  p: 2,
                  borderLeft: "4px solid",
                  borderColor: stat.color
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                  {stat.label}
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  {stat.value}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            mb: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              minWidth: "auto"
            }
          }}
        >
          {tabLabels.map((label, idx) => (
            <Tab
              key={idx}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{label}</span>
                  {idx === 1 && withdrawals.data.filter((w) => w.status === 0).length > 0 && (
                    <Chip
                      label={withdrawals.data.filter((w) => w.status === 0).length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 10,
                        bgcolor: "error.main",
                        color: "white",
                        fontWeight: 700
                      }}
                    />
                  )}
                </Stack>
              }
            />
          ))}
        </Tabs>

        {/* Tab Content */}
        {renderTabContent()}
      </Container>

      {/* Approve Modal */}
      <Modal
        open={modalState.showApprove}
        onClose={handleModalClose}
        slotProps={{ backdrop: { sx: { backdropFilter: "blur(8px)", backgroundColor: "rgba(15, 23, 42, 0.4)" } } }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 480,
            bgcolor: "background.paper",
            borderRadius: "24px",
            boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider"
          }}
        >
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: "50%",
                  bgcolor: "rgba(22, 163, 74, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "4px solid rgba(22, 163, 74, 0.2)"
                }}
              >
                <CheckCircle sx={{ color: "success.main", fontSize: 40 }} />
              </Box>
            </Box>
            <Typography variant="h5" fontWeight={700} mb={1}>
              Confirm Approval
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Are you sure you want to approve the withdrawal of{" "}
              <strong>{formatCurrency(modalState.selectedRequest?.amount)}</strong> for{" "}
              <strong>{modalState.selectedRequest?.m_name}</strong>?
            </Typography>
            <Box sx={{ textAlign: "left", mb: 3 }}>
              <Typography variant="body2" fontWeight={600} color="text.secondary" mb={1}>
                Payment Screenshot <Typography component="span" color="error.main">*</Typography>
              </Typography>
              <input
                ref={screenshotInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setPaymentScreenshot(file);
                    setScreenshotPreview(URL.createObjectURL(file));
                    setModalState((prev) => ({ ...prev, error: null }));
                  }
                }}
              />
              {screenshotPreview ? (
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <Box
                    component="img"
                    src={screenshotPreview}
                    alt="Payment Screenshot"
                    sx={{
                      width: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                      borderRadius: "12px",
                      border: "1px solid",
                      borderColor: "divider"
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setPaymentScreenshot(null);
                      setScreenshotPreview("");
                      if (screenshotInputRef.current) screenshotInputRef.current.value = "";
                    }}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      bgcolor: "error.main",
                      color: "white",
                      "&:hover": { bgcolor: "error.dark" }
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  onClick={() => screenshotInputRef.current?.click()}
                  sx={{
                    border: "2px dashed",
                    borderColor: "divider",
                    borderRadius: "12px",
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" }
                  }}
                >
                  <CloudUpload sx={{ fontSize: 36, color: "text.secondary", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Click to upload payment screenshot
                  </Typography>
                </Box>
              )}
            </Box>
            {modalState.error && (
              <Typography variant="body2" color="error.main" mb={2} textAlign="left">
                {modalState.error}
              </Typography>
            )}
            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleModalClose}
                disabled={modalState.processing}
                sx={{ py: 1.5, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApproveConfirm}
                disabled={modalState.processing}
                startIcon={modalState.processing && <CircularProgress size={18} color="inherit" />}
                sx={{ py: 1.5, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
              >
                Confirm Approve
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>

      {/* Reject Modal */}
      <Modal
        open={modalState.showReject}
        onClose={handleModalClose}
        slotProps={{ backdrop: { sx: { backdropFilter: "blur(8px)", backgroundColor: "rgba(15, 23, 42, 0.4)" } } }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 480,
            bgcolor: "background.paper",
            borderRadius: "16px",
            boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider"
          }}
        >
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: "50%",
                bgcolor: "error.light",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3
              }}
            >
              <Cancel sx={{ color: "error.main", fontSize: 40 }} />
            </Box>
            <Typography variant="h5" fontWeight={700} mb={1}>
              Reject Withdrawal Request
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              This action cannot be undone.
            </Typography>
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: 2,
                p: 2,
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                textAlign: "left"
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", fontSize: 10 }}>
                  Member
                </Typography>
                <Typography variant="body2" fontWeight={600} color="primary">
                  {modalState.selectedRequest?.m_name}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", fontSize: 10 }}>
                  Amount
                </Typography>
                <Typography variant="h6" fontWeight={700} color="error">
                  {formatCurrency(modalState.selectedRequest?.amount)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: "left", mb: 1 }}>
              <Typography variant="body2" fontWeight={600} color="text.secondary" mb={1}>
                Reason for rejection <Typography component="span" color="error.main">*</Typography>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Please provide a clear reason..."
                value={modalState.rejectReason}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setModalState((prev) => ({ ...prev, rejectReason: e.target.value, error: null }));
                  }
                }}
                error={!!modalState.error}
                helperText={modalState.error}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {modalState.rejectReason.length} / 500
                </Typography>
              </Box>
            </Box>
          </Box>
          <Stack
            direction="row"
            spacing={2}
            sx={{ p: 3, bgcolor: "action.hover", borderTop: "1px solid", borderColor: "divider" }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={handleModalClose}
              disabled={modalState.processing}
              sx={{ py: 1.5, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="error"
              onClick={handleRejectConfirm}
              disabled={modalState.processing || !modalState.rejectReason.trim()}
              startIcon={modalState.processing && <CircularProgress size={18} color="inherit" />}
              sx={{ py: 1.5, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
            >
              Confirm Reject
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* QR Code Modal */}
      <Modal
        open={qrModal}
        onClose={() => {
          setQrModal(false);
          setQrRow(null);
          setQrDataUrl("");
        }}
        slotProps={{ backdrop: { sx: { backdropFilter: "blur(8px)", backgroundColor: "rgba(15, 23, 42, 0.4)" } } }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 380,
            bgcolor: "background.paper",
            borderRadius: "24px",
            boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            p: 4,
            textAlign: "center"
          }}
        >
          <Typography variant="h6" fontWeight={700} mb={1}>
            UPI QR Code
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Scan to pay <strong>{formatCurrency(qrRow?.amount)}</strong>
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            {qrDataUrl ? (
              <Box component="img" src={qrDataUrl} alt="UPI QR Code" sx={{ width: 220, height: 220 }} />
            ) : (
              <CircularProgress />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" mb={3}>
            UPI ID: {qrRow?.upi_id}
          </Typography>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              setQrModal(false);
              setQrRow(null);
              setQrDataUrl("");
            }}
            sx={{ py: 1.5, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
        </Box>
      </Modal>

      {/* Payment Screenshot Viewer Modal */}
      <Modal
        open={screenshotModal}
        onClose={() => {
          setScreenshotModal(false);
          setScreenshotViewUrl("");
          setZoom(1);
          setPan({ x: 0, y: 0 });
        }}
        slotProps={{ backdrop: { sx: { backdropFilter: "blur(8px)", backgroundColor: "rgba(15, 23, 42, 0.6)" } } }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90vw",
            maxWidth: 600,
            height: "85vh",
            bgcolor: "background.paper",
            borderRadius: "24px",
            boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Box sx={{ p: 3, textAlign: "center", borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Payment Screenshot
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
              <Tooltip title="Zoom Out">
                <IconButton
                  size="small"
                  onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))}
                  disabled={zoom <= 0.25}
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <ZoomOut fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 50, textAlign: "center" }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <Tooltip title="Zoom In">
                <IconButton
                  size="small"
                  onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
                  disabled={zoom >= 4}
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <ZoomIn fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset All">
                <IconButton
                  size="small"
                  onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
                  disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <RestartAlt fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => {
                  setScreenshotModal(false);
                  setScreenshotViewUrl("");
                  setZoom(1);
                  setPan({ x: 0, y: 0 });
                }}
              >
                <Cancel />
              </IconButton>
            </Stack>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              minHeight: 300,
              cursor: zoom > 1 ? (dragRef.current.active ? "grabbing" : "grab") : "default"
            }}
            onWheel={(e) => {
              e.preventDefault();
              setZoom((z) => {
                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                return Math.min(4, Math.max(0.25, +(z + delta).toFixed(2)));
              });
            }}
            onMouseDown={(e) => {
              if (zoom <= 1) return;
              e.preventDefault();
              dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
            }}
            onMouseMove={(e) => {
              if (!dragRef.current.active) return;
              const dx = e.clientX - dragRef.current.startX;
              const dy = e.clientY - dragRef.current.startY;
              setPan({ x: dragRef.current.panX + dx, y: dragRef.current.panY + dy });
            }}
            onMouseUp={() => { dragRef.current.active = false; }}
            onMouseLeave={() => { dragRef.current.active = false; }}
          >
            {screenshotViewUrl ? (
              <Box
                component="img"
                src={screenshotViewUrl}
                alt="Payment Screenshot"
                draggable={false}
                sx={{
                  width: "100%",
                  maxHeight: "calc(85vh - 130px)",
                  objectFit: "contain",
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: "center center",
                  transition: dragRef.current.active ? "none" : "transform 0.15s ease",
                  userSelect: "none"
                }}
              />
            ) : (
              <CircularProgress />
            )}
          </Box>
        </Box>
      </Modal>
    </Page>
  );
}
